import { DocumentNode, useLazyQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import {
    findMissingTrackingNumbers,
    hasLastNToken,
    MAX_TRACKING_HIT_QUERY_SIZE,
    splitTrackingNumbers,
} from './trackingNumbers';

interface UseNotFoundTrackingNumbersOptions {
    /** 本次查询（已生效筛选）的运单号原始输入 */
    trackingNumbers: string | null | undefined;
    /** 本次查询的总条数 */
    totalElements: number;
    /** 列表是否加载中 */
    loading: boolean;
    /** 列表查询文档（与页面同一入口，保证数据范围 / 权限一致） */
    query: DocumentNode;
    /** 响应里列表字段名，如 getOrderListingForCentral / getOrderListingForWMS */
    dataKey: string;
    /** 列表查询变量（filters + pageRequest），补查时只改分页 */
    variables: { filters: any; pageRequest?: Record<string, any> } | null | undefined;
    skip?: boolean;
}

/**
 * 多个运单号查询、部分未查到时，算出未查到的运单号。
 *
 * 列表是分页的，当前页拿不全命中运单号，所以在「可能有缺失」时用同一入口补查一页（≤ 200 条）：
 * - 输入（去重、去空后）≥ 2 个，且有结果（全部未查到走 R4）；
 * - 输入 > 200 个、或结果 > 200 条时无法一页拿全，不做提示；
 * - 总条数 ≥ 输入个数且没有 6 位尾号 token 时不可能有缺失（运单号唯一），不补查。
 */
export function useNotFoundTrackingNumbers({
    trackingNumbers,
    totalElements,
    loading,
    query,
    dataKey,
    variables,
    skip = false,
}: UseNotFoundTrackingNumbersOptions): string[] {
    const inputs = useMemo(() => splitTrackingNumbers(trackingNumbers), [trackingNumbers]);

    const needCheck =
        !skip &&
        !loading &&
        inputs.length >= 2 &&
        inputs.length <= MAX_TRACKING_HIT_QUERY_SIZE &&
        totalElements > 0 &&
        totalElements <= MAX_TRACKING_HIT_QUERY_SIZE &&
        (totalElements < inputs.length || hasLastNToken(inputs));

    const filtersKey = JSON.stringify(variables?.filters ?? null);
    const [runHitQuery, { data, loading: hitLoading, variables: hitVariables }] = useLazyQuery(query, {
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (!needCheck || !variables) {
            return;
        }
        runHitQuery({
            variables: {
                ...variables,
                pageRequest: { ...(variables.pageRequest ?? {}), page: 1, size: MAX_TRACKING_HIT_QUERY_SIZE },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needCheck, filtersKey, totalElements]);

    return useMemo(() => {
        // 补查结果须对应当前筛选，避免筛选切换后短暂展示上一次的结果
        if (!needCheck || hitLoading || !data || JSON.stringify(hitVariables?.filters ?? null) !== filtersKey) {
            return [];
        }
        const content: Array<{ trackingNumber?: string | null }> = (data as any)?.[dataKey]?.content ?? [];
        return findMissingTrackingNumbers(inputs, content.map((order) => order.trackingNumber));
    }, [needCheck, hitLoading, data, hitVariables, filtersKey, dataKey, inputs]);
}
